export const GEMINI_TOOL_DECLARATIONS = [
  {
    name: 'show_alert',
    description: 'Show an alert dialog to the user with a text message',
    parameters: {
      type: 'OBJECT',
      properties: {
        message: {
          type: 'STRING',
          description: 'The text message to display in the alert',
        },
      },
      required: ['message'],
    },
  },
  {
    name: 'add_overlay',
    description: `Add HTML/SVG annotation on top of the 150x200px camera video overlay. The overlay div has position:relative, so use position:absolute on all children. Coordinates: top-left=(0,0), bottom-right=(150,200).

PREFERRED PATTERNS (use these exact patterns, adjusting coordinates):

Circle highlight around an object:
<svg style="position:absolute;top:0;left:0;width:150px;height:200px"><circle cx="75" cy="80" r="30" fill="none" stroke="red" stroke-width="3"/></svg>

Arrow pointing at something (pointing down-right to a spot at x=100,y=120):
<svg style="position:absolute;top:0;left:0;width:150px;height:200px"><line x1="60" y1="60" x2="95" y2="115" stroke="yellow" stroke-width="3"/><polygon points="100,120 88,110 98,108" fill="yellow"/></svg>

Text label at bottom:
<div style="position:absolute;bottom:8px;left:50%;transform:translateX(-50%);color:white;font-size:11px;background:rgba(0,0,0,0.7);padding:2px 8px;border-radius:4px;white-space:nowrap">Label text</div>

Rectangular highlight:
<svg style="position:absolute;top:0;left:0;width:150px;height:200px"><rect x="30" y="40" width="90" height="60" fill="none" stroke="lime" stroke-width="2" rx="4"/></svg>

Full border glow:
<div style="position:absolute;top:0;left:0;width:100%;height:100%;border:3px solid rgba(0,255,0,0.6);border-radius:12px;box-sizing:border-box"></div>`,
    parameters: {
      type: 'OBJECT',
      properties: {
        html: {
          type: 'STRING',
          description: 'HTML/SVG string to inject. Use the patterns from the description. Always use inline styles with position:absolute.',
        },
      },
      required: ['html'],
    },
  },
  {
    name: 'highlight_object',
    description: `Highlight an object in the camera feed by providing its bounding box and shape. The overlay is 150x200px. Coordinates: top-left=(0,0), bottom-right=(150,200). Video is mirrored (user's left = right side).

IMPORTANT: Provide the bounding box as the OUTER EDGES of where the object appears in the frame. Think of it as: what are the leftmost, topmost, rightmost, and bottommost pixels the object touches? Be GENEROUS — it's better to be slightly too large than too small.`,
    parameters: {
      type: 'OBJECT',
      properties: {
        label: {
          type: 'STRING',
          description: 'Name of the object, e.g. "mug", "phone", "face"',
        },
        x: {
          type: 'NUMBER',
          description: 'Left edge X coordinate (0-150)',
        },
        y: {
          type: 'NUMBER',
          description: 'Top edge Y coordinate (0-200)',
        },
        width: {
          type: 'NUMBER',
          description: 'Width of bounding box in pixels',
        },
        height: {
          type: 'NUMBER',
          description: 'Height of bounding box in pixels',
        },
        shape: {
          type: 'STRING',
          description: 'Shape of the object: "rectangle" (phones, books, screens), "rounded" (mugs, bottles, jars), "ellipse" (faces, balls, plates), "circle" (clocks, cups from top)',
        },
        color: {
          type: 'STRING',
          description: 'CSS color for the outline, e.g. "red", "lime", "cyan"',
        },
      },
      required: ['label', 'x', 'y', 'width', 'height', 'shape'],
    },
  },
  {
    name: 'mark_objects',
    description: `Place glowing dots at the center of one or more objects in the camera feed. The overlay is 150x200px. Coordinates: top-left=(0,0), bottom-right=(150,200). Video is mirrored (user's left = right side of frame). Each dot has a label shown below it. This is the PREFERRED tool for pointing at objects — simpler and more accurate than highlight_object.`,
    parameters: {
      type: 'OBJECT',
      properties: {
        dots: {
          type: 'STRING',
          description: 'JSON array of objects with x, y, label, and optional color. Example: [{"x":75,"y":60,"label":"mug"},{"x":40,"y":100,"label":"phone","color":"lime"}]',
        },
      },
      required: ['dots'],
    },
  },
  {
    name: 'clear_overlay',
    description: 'Remove all annotations from the overlay. ALWAYS call this before adding new annotations to prevent stacking.',
    parameters: {
      type: 'OBJECT',
      properties: {},
    },
  },
  {
    name: 'prompt_password_change',
    description: 'Show a secure password input form to the user. Use this when the user wants to set or change their password. The UI will handle input securely — never ask the user to say their password out loud.',
    parameters: {
      type: 'OBJECT',
      properties: {},
    },
  },
  {
    name: 'get_conversation_history',
    description: 'Retrieve the prior text conversation history between the user and the core-agent. Call this at the start of the session to understand what has already been discussed. After receiving the history, wait silently for the user to speak first. Do NOT greet the user or speak unprompted.',
    parameters: {
      type: 'OBJECT',
      properties: {},
    },
  },
];

export function executeToolCall(fc: any, overlayEl: HTMLDivElement | null, showAlert?: (msg: string) => void): string {
  switch (fc.name) {
    case 'show_alert':
      globalThis.alert(fc.args.message);
      return 'Alert displayed successfully';

    case 'add_overlay':
      if (overlayEl) {
        overlayEl.insertAdjacentHTML('beforeend', fc.args.html);
        return 'Overlay element added successfully';
      }
      return 'Overlay container not found';

    case 'highlight_object':
      if (overlayEl) {
        try {
          const color = fc.args.color || 'cyan';
          const label = fc.args.label || '';
          const pad = 6;
          const x = (fc.args.x || 0) - pad;
          const y = (fc.args.y || 0) - pad;
          const w = (fc.args.width || 50) + pad * 2;
          const h = (fc.args.height || 50) + pad * 2;
          const shape = fc.args.shape || 'rounded';
          let shapeEl = '';
          if (shape === 'ellipse' || shape === 'circle') {
            const cx = x + w / 2;
            const cy = y + h / 2;
            shapeEl = `<ellipse cx="${cx}" cy="${cy}" rx="${w / 2}" ry="${h / 2}" fill="none" stroke="${color}" stroke-width="2.5"/>`;
          } else {
            const rx = shape === 'rounded' ? Math.min(w, h) * 0.15 : shape === 'rectangle' ? 4 : 8;
            shapeEl = `<rect x="${x}" y="${y}" width="${w}" height="${h}" rx="${rx}" fill="none" stroke="${color}" stroke-width="2.5"/>`;
          }
          const labelX = x + w / 2;
          const labelY = Math.max(10, y - 6);
          const svg = `<svg style="position:absolute;top:0;left:0;width:150px;height:200px;overflow:visible">
            ${shapeEl}
            ${label ? `<text x="${labelX}" y="${labelY}" text-anchor="middle" fill="white" font-size="10" font-family="sans-serif" font-weight="bold" paint-order="stroke" stroke="rgba(0,0,0,0.7)" stroke-width="3">${label}</text>` : ''}
          </svg>`;
          overlayEl.innerHTML = '';
          overlayEl.insertAdjacentHTML('beforeend', svg);
          return `Highlighted "${label}" at (${fc.args.x},${fc.args.y}) ${w}x${h} shape=${shape}`;
        } catch (e) {
          return `Failed to render highlight: ${e}`;
        }
      }
      return 'Overlay container not found';

    case 'mark_objects':
      if (overlayEl) {
        try {
          const dots = JSON.parse(fc.args.dots);
          let svgContent = '';
          for (const dot of dots) {
            const color = dot.color || 'cyan';
            svgContent += `
              <circle cx="${dot.x}" cy="${dot.y}" r="12" fill="${color}" opacity="0.25">
                <animate attributeName="r" values="10;16;10" dur="1.5s" repeatCount="indefinite"/>
                <animate attributeName="opacity" values="0.3;0.1;0.3" dur="1.5s" repeatCount="indefinite"/>
              </circle>
              <circle cx="${dot.x}" cy="${dot.y}" r="5" fill="${color}" stroke="white" stroke-width="1.5"/>
              <text x="${dot.x}" y="${dot.y + 18}" text-anchor="middle" fill="white" font-size="9" font-family="sans-serif" font-weight="bold" paint-order="stroke" stroke="rgba(0,0,0,0.8)" stroke-width="2.5">${dot.label || ''}</text>`;
          }
          const svg = `<svg style="position:absolute;top:0;left:0;width:150px;height:200px;overflow:visible">${svgContent}</svg>`;
          overlayEl.innerHTML = '';
          overlayEl.insertAdjacentHTML('beforeend', svg);
          return `Marked ${dots.length} object(s)`;
        } catch (e) {
          return `Failed to parse dots: ${e}`;
        }
      }
      return 'Overlay container not found';

    case 'clear_overlay':
      if (overlayEl) {
        overlayEl.innerHTML = '';
        return 'Overlay cleared';
      }
      return 'Overlay container not found';

    default:
      return `Unknown tool: ${fc.name}`;
  }
}
